import { LOCALES, type Locale } from '@aysar/contracts';
import { Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/DropdownMenu/DropdownMenu';
import { localeChanged, selectLocale } from '@/shared/store/reducers/preferences/preferences.slice';
import { useAppDispatch, useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';

/**
 * Switches the portal's language.
 *
 * Each option is labelled by its ENDONYM, from the kit catalogue — the same
 * reasoning as `LocaleTabs`. Dispatching is all this does: `DirectionProvider`
 * watches the slice and moves `lang`/`dir` on `<html>` plus i18next's language,
 * so no component has to remember the three things a language change implies.
 */
export function LocaleToggle() {
  const { t } = useTranslation('uiKit');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const locale = useAppSelector(selectLocale);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={tCommon('shell.locale')}>
          <Languages aria-hidden className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {LOCALES.map((option: Locale) => (
          <DropdownMenuItem
            key={option}
            onSelect={() => {
              dispatch(localeChanged(option));
            }}
            aria-current={locale === option ? 'true' : undefined}
            className={locale === option ? 'bg-secondary' : undefined}
          >
            {t(`locale.${option}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
